/* eslint-disable no-nested-ternary */
import { selectLabsAccess, selectToken } from '@redux/slices/userSlice';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { coreService } from 'network-utils';
import { FullPageLoader, FooterVariant, CodeSection, LanguageMapping } from 'learning-panel-components';
import { useGetLabInfo, getLabInfo } from '@queries/core/labs';
import { Progress, getTags } from 'utils';
import PreviewPage from './previewPage/previewPage';
import Game from './game/game';
import styles from './lab.module.scss';

const LabContainer = () => {
    const [labState, setLabState] = useState<'preview' | 'game'>('preview');
    const [labData, setLabData] = useState<coreService.Lab | undefined>(undefined);
    const [currentQuestionNumber, setCurrentQuestionNumber] = useState<number>(1);
    const [isGameDone, setIsGameDone] = useState<boolean>(false);
    const [isEndGame, setIsEndGame] = useState<boolean>(false);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
    const [isOutOfAttemps, setIsOutOfAttemps] = useState<boolean>(false);
    const [attemptsLeftNumber, setAttemptsLeftNumber] = useState<number>(0);
    const [remediationResponse, setRemediationResponse] = useState<
        coreService.SubmitVulnerabilityFixQuestionResponse | undefined
    >(undefined);
    const [remediationCode, setRemediationCode] = useState<string>('');
    const [lastRemediationAttempt, setLastRemediationAttempt] = useState<string | undefined>(undefined);
    const [codeSections, setCodeSections] = useState<CodeSection[]>([]);
    const [isCurrentResults, setIsCurrentResults] = useState<boolean>(false);

    const isFirstFetch = useRef(true);
    const isFirstEffect = useRef(true);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { labUuid } = useParams();
    const token = useSelector(selectToken);
    const labsAccess = useSelector(selectLabsAccess);

    const { data: labInfo, isLoading: isLoadingLabInfo } = useGetLabInfo(token, labUuid || '', labsAccess);

    const currentQuestionMemo = useMemo(() => {
        if (!labData) return undefined;

        return labData.questions[currentQuestionNumber - 1];
    }, [currentQuestionNumber, labData]);

    const hasAnsweredCorrectlyMemo = useMemo(() => {
        if (!currentQuestionMemo) return false;
        return currentQuestionMemo.userAnswers.some(answer => answer.result === 100);
    }, [currentQuestionMemo]);

    const footerVariant = useMemo(() => {
        const isNotLastQuestion = currentQuestionNumber < (labData?.questions?.length || 0);
        if (isLoadingSubmit) return FooterVariant.LOADING;
        if (
            (isGameDone && isNotLastQuestion) ||
            (isNotLastQuestion && !isCurrentResults && (isOutOfAttemps || hasAnsweredCorrectlyMemo))
        )
            return FooterVariant.READ_ONLY;
        if (isGameDone) return FooterVariant.DONE;
        if (isEndGame) return FooterVariant.END_GAME;
        if (remediationResponse) return FooterVariant.RESULT;
        if (remediationCode.length > 0 && !isOutOfAttemps) return FooterVariant.SUBMIT;
        return FooterVariant.SUBMIT_DISABLED;
    }, [isGameDone, isEndGame, remediationResponse, remediationCode, isLoadingSubmit, labData, currentQuestionNumber]);

    useEffect(() => {
        setRemediationResponse(undefined);
        setRemediationCode('');
        setCodeSections([]);
        setIsCurrentResults(false);

        if (!labData) return;
        const currentQuestion = labData.questions[currentQuestionNumber - 1];

        // load last remediation attempt
        if (
            currentQuestion.userAnswers.length &&
            ![FooterVariant.DONE, FooterVariant.END_GAME].includes(footerVariant)
        ) {
            const userAnswersIndex = currentQuestion.userAnswers.length - 1;
            setRemediationCode(currentQuestion.userAnswers[userAnswersIndex].userRemediation);
            setRemediationResponse(currentQuestion.userAnswers[userAnswersIndex]);
        }
    }, [currentQuestionNumber]);

    useEffect(() => {
        if (!labData) return;
        const currentQuestion = labData.questions[currentQuestionNumber - 1];

        // load last remediation attempt for first question render
        if (
            currentQuestion.userAnswers.length &&
            isFirstEffect.current &&
            ![FooterVariant.DONE, FooterVariant.END_GAME].includes(footerVariant)
        ) {
            const userAnswersIndex = currentQuestion.userAnswers.length - 1;
            setRemediationCode(currentQuestion.userAnswers[userAnswersIndex].userRemediation);
            setRemediationResponse(currentQuestion.userAnswers[userAnswersIndex]);
            isFirstEffect.current = false;
        }
    }, [labData]);

    useEffect(() => {
        if (labInfo && labInfo.info) {
            setLabData(labInfo.info);
        }
    }, [labInfo]);

    useEffect(() => {
        if (!labData) return;
        const currentQuestion = labData.questions[currentQuestionNumber - 1];

        // load highest result from user answers
        if (
            [FooterVariant.DONE, FooterVariant.END_GAME].includes(footerVariant) &&
            currentQuestion.userAnswers.length
        ) {
            const lastHighestResult = currentQuestion.userAnswers.reduce(
                (highest: coreService.UserAnswer, answer: coreService.UserAnswer) => {
                    if (!highest || answer.result >= highest.result) {
                        return answer;
                    }
                    return highest;
                }
            );

            if (lastHighestResult) {
                setRemediationCode(lastHighestResult.userRemediation);
                setRemediationResponse(lastHighestResult);
            }
        }
    }, [currentQuestionNumber, labData, footerVariant]);

    useEffect(() => {
        if (!labData) return;

        const isCompletedStatus = labData.status === Progress.COMPLETED;
        if (isFirstFetch.current && isCompletedStatus) {
            setIsGameDone(true);
        }
        isFirstFetch.current = false;

        const totalAttemptsLeft = labData.questions.find(question => question.attemptsLeft);
        setIsEndGame(!totalAttemptsLeft);

        const currentQuestion = labData.questions[currentQuestionNumber - 1];
        const { attemptsLeft } = currentQuestion;
        setIsOutOfAttemps(!attemptsLeft);
        setAttemptsLeftNumber(attemptsLeft || 0);

        if (currentQuestion.userAnswers.length) {
            const userAnswersIndex = currentQuestion.userAnswers.length - 1;
            setLastRemediationAttempt(currentQuestion.userAnswers[userAnswersIndex].userRemediation);
        } else {
            setLastRemediationAttempt(undefined);
        }
    }, [currentQuestionNumber, labData]);

    useEffect(() => {
        setCodeSections(prepareCodeSections());
    }, [remediationResponse]);

    const prepareCodeSections = () => {
        if (!remediationResponse) return [];

        const codeSections: CodeSection[] = [];
        remediationResponse.correctLines?.map((line: any) => {
            codeSections.push({
                isCorrect: true,
                startLine: line?.startLine,
                endLine: line?.endLine,
                description: line?.description
            });
        });

        remediationResponse.wrongLines?.map((line: any) => {
            codeSections.push({
                isCorrect: false,
                startLine: line?.startLine,
                endLine: line?.endLine
            });
        });
        return codeSections;
    };

    const relatedContentMemo = useMemo(() => {
        if (!labData) return { course: [], lesson: [], challenge: [] };

        const currentQuestionIndex = currentQuestionNumber - 1;
        const question = labData.questions[currentQuestionIndex];

        if (!question) return { course: [], lesson: [], challenge: [] };

        const relatedContent = {
            course: question.helpfulContent.courses || [],
            lesson: question.helpfulContent.lessons || [],
            challenge: question.helpfulContent.challenges || []
        };

        return relatedContent;
    }, [currentQuestionNumber, labData]);

    const codeMemo = useMemo(() => {
        if (!labData) return '';

        return currentQuestionMemo?.vulnerableCode;
    }, [currentQuestionNumber, labData]);

    const rankMemo = useMemo(() => {
        if (!labData || !currentQuestionMemo) return { rankNumber: '', rankLevel: 'medium' };

        return {
            rankNumber: currentQuestionMemo.rank.toString(),
            rankLevel: 'medium'
        };
    }, [currentQuestionNumber, labData]);

    const linksMemo = useMemo(() => {
        if (!labData) return [];

        const links = getTags(currentQuestionMemo, 'cweId', 'owaspId', 'sansId');
        return links;
    }, [currentQuestionNumber, labData]);

    const userQuestions = useMemo(() => {
        if (!labData) return [];

        return labData.questions.map((question, index) => {
            let highestResult;
            if (question.userAnswers.length > 0) {
                highestResult = question.userAnswers.reduce((acc, curr) => {
                    return acc.result > curr.result ? acc : curr;
                });
            }

            return {
                questionNumber: index + 1,
                percentages: highestResult?.result
            };
        });
    }, [labData]);

    const userScoreMemo = useMemo(() => {
        if (!labData) return 0;

        return labData.questions.reduce((total, question) => {
            const highestScore = Math.max(...question.userAnswers.map(answer => answer.points));
            if (highestScore > 0) {
                return total + highestScore;
            }
            return total;
        }, 0);
    }, [labData]);

    const submitAnswer = async () => {
        if (!labData || !currentQuestionMemo) {
            return;
        }
        setIsLoadingSubmit(true);
        const result = await coreService.labsApi.submitLabQuestionUserRemediation(token, {
            labUuid: labUuid || '',
            vulnerabilityFixQuestionUuid: currentQuestionMemo.uuid,
            userRemediation: remediationCode
        });

        if (result.success) {
            queryClient.invalidateQueries([getLabInfo]);
            setRemediationResponse(result);
            setIsCurrentResults(true);
        }
        setIsLoadingSubmit(false);
    };

    const startLab = async () => {
        if (!labUuid) return;
        const result = await coreService.labsApi.startLab(labUuid, token);

        if (result?.success) {
            queryClient.invalidateQueries([getLabInfo]);
        }
    };

    const onCodeChange = (code: string) => {
        setRemediationCode(code);
    };

    const tryAgain = () => {
        setRemediationCode('');
        setRemediationResponse(undefined);
        setCodeSections([]);
    };

    const skipTrying = () => {
        setCurrentQuestionNumber(currentQuestionNumber === labData?.questions.length ? 1 : currentQuestionNumber + 1);
    };

    const next = () => {
        setCurrentQuestionNumber(currentQuestionNumber === labData?.questions.length ? 1 : currentQuestionNumber + 1);
    };

    const submit = () => {
        submitAnswer();
    };

    const endGame = () => {
        navigate('/app/home');
    };

    const done = () => {
        navigate('/app/home');
    };

    const onMainAction = () => {
        switch (footerVariant) {
            case FooterVariant.READ_ONLY:
                next();
                break;
            case FooterVariant.DONE:
                done();
                break;
            case FooterVariant.END_GAME:
                endGame();
                break;
            case FooterVariant.RESULT:
                if (isOutOfAttemps) {
                    next();
                } else {
                    tryAgain();
                }
                break;
            case FooterVariant.SUBMIT:
                submit();
                break;
            case FooterVariant.SUBMIT_DISABLED:
                break;
            case FooterVariant.LOADING:
                break;
        }
    };

    const onSecondaryAction = () => {
        switch (footerVariant) {
            case FooterVariant.RESULT:
                if (!isOutOfAttemps) {
                    skipTrying();
                }
                break;
        }
    };

    const isCompleted = [FooterVariant.DONE, FooterVariant.END_GAME].includes(footerVariant);

    const languageMemo = useMemo(() => {
        if (!currentQuestionMemo) return 'javascript';
        return (
            LanguageMapping[currentQuestionMemo.language as keyof typeof LanguageMapping] ||
            currentQuestionMemo.language
        );
    }, [currentQuestionMemo]);

    return isLoadingLabInfo || !labData ? (
        <FullPageLoader />
    ) : (
        labData && (
            <div className={styles.lab}>
                <div className={styles.content}>
                    {labState === 'preview' && (
                        <PreviewPage
                            {...labData}
                            onScrollToRemediate={() => {
                                setLabState('game');
                                startLab();
                            }}
                            isReadOnly={isCompleted}
                            userQuestions={userQuestions}
                            totalScore={userScoreMemo}
                            onViewDrill={() => setLabState('game')}
                        />
                    )}
                    {labState === 'game' && (
                        <Game
                            name={labData?.name || ''}
                            potentialScore={labData?.potentialScore || 0}
                            vulDescription={labData?.questions[currentQuestionNumber - 1]?.description || ''}
                            rankNumber={rankMemo.rankNumber}
                            rankLevel={rankMemo.rankLevel as 'medium' | 'high' | 'low'}
                            links={linksMemo}
                            relatedContent={relatedContentMemo}
                            vulName={labData?.questions[currentQuestionNumber - 1]?.displayName || ''}
                            questionNumber={currentQuestionNumber}
                            totalQuestions={labData?.questions.length || 0}
                            onNavigateToNextQuestion={() => setCurrentQuestionNumber(currentQuestionNumber + 1)}
                            onNavigateToPreviousQuestion={() => setCurrentQuestionNumber(currentQuestionNumber - 1)}
                            userScore={userScoreMemo}
                            code={codeMemo}
                            outOfTries={isOutOfAttemps}
                            footerVariant={footerVariant}
                            onCodeChange={onCodeChange}
                            remediationCode={remediationCode}
                            onMainAction={onMainAction}
                            onSecondaryAction={onSecondaryAction}
                            userQuestions={userQuestions}
                            onNavigateToQuestion={(questionNumber: number) => () =>
                                setCurrentQuestionNumber(questionNumber)}
                            lastRemediationAttempt={lastRemediationAttempt}
                            percentage={remediationResponse?.result || 0}
                            codeSections={codeSections}
                            attemptsLeftNumber={attemptsLeftNumber}
                            completedMask={isCompleted}
                            language={languageMemo}
                            isGameDone={isGameDone}
                        />
                    )}
                </div>
            </div>
        )
    );
};

export default LabContainer;
